import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, CalendarDays, MapPin, Map, Ticket, DollarSign, Info,
  Sparkles, Globe, Users, Target, Bell, Share2, List, AlignLeft,
  GripVertical, Plus, Bot, CheckCircle2, Plane, BedDouble,
  RefreshCw, Paperclip, ListChecks, Route, Camera,
  ChevronRight, Zap,
} from '../../components/ui/Icon';
import { Icon } from '../../components/ui/Icon';

// ── Section definitions ────────────────────────────────────────────────────────

interface DocSection {
  id: string;
  icon: React.ElementType;
  color: string;
  gradient: string;
  title: string;
  subtitle: string;
  content: DocBlock[];
}

type DocBlock =
  | { type: 'p'; text: string }
  | { type: 'tip'; text: string }
  | { type: 'steps'; items: string[] }
  | { type: 'features'; items: { icon: React.ElementType; label: string; desc: string }[] }
  | { type: 'warning'; text: string };

function getSections(t: (key: string) => string): DocSection[] {
  return [
    {
      id: 'itinerary',
      icon: CalendarDays,
      color: 'text-teal-600 dark:text-teal-400',
      gradient: 'from-teal-500 to-teal-700',
      title: t('docs.itinerary.title'),
      subtitle: t('docs.itinerary.subtitle'),
      content: [
        { type: 'p', text: t('docs.itinerary.p1') },
        { type: 'features', items: [
          { icon: Plus,         label: t('docs.itinerary.feat_addDay_label'), desc: t('docs.itinerary.feat_addDay_desc') },
          { icon: GripVertical, label: t('docs.itinerary.feat_drag_label'),   desc: t('docs.itinerary.feat_drag_desc') },
          { icon: Bot,          label: t('docs.itinerary.feat_ai_label'),     desc: t('docs.itinerary.feat_ai_desc') },
        ]},
        { type: 'tip', text: t('docs.itinerary.tip1') },
        { type: 'steps', items: [
          t('docs.itinerary.step1'), t('docs.itinerary.step2'),
          t('docs.itinerary.step3'), t('docs.itinerary.step4'),
        ]},
      ],
    },
    {
      id: 'places',
      icon: MapPin,
      color: 'text-violet-600 dark:text-violet-400',
      gradient: 'from-violet-500 to-purple-700',
      title: t('docs.places.title'),
      subtitle: t('docs.places.subtitle'),
      content: [
        { type: 'p', text: t('docs.places.p1') },
        { type: 'features', items: [
          { icon: Plus,         label: t('docs.places.feat_add_label'),     desc: t('docs.places.feat_add_desc') },
          { icon: CheckCircle2, label: t('docs.places.feat_visited_label'), desc: t('docs.places.feat_visited_desc') },
          { icon: Bot,          label: t('docs.places.feat_ai_label'),      desc: t('docs.places.feat_ai_desc') },
        ]},
        { type: 'tip', text: t('docs.places.tip1') },
      ],
    },
    {
      id: 'map',
      icon: Map,
      color: 'text-sky-600 dark:text-sky-400',
      gradient: 'from-sky-500 to-blue-700',
      title: t('docs.map.title'),
      subtitle: t('docs.map.subtitle'),
      content: [
        { type: 'p', text: t('docs.map.p1') },
        { type: 'features', items: [
          { icon: Route,        label: t('docs.map.feat_route_label'),   desc: t('docs.map.feat_route_desc') },
          { icon: GripVertical, label: t('docs.map.feat_reorder_label'), desc: t('docs.map.feat_reorder_desc') },
          { icon: Sparkles,     label: t('docs.map.feat_ai_label'),      desc: t('docs.map.feat_ai_desc') },
        ]},
        { type: 'tip', text: t('docs.map.tip1') },
      ],
    },
    {
      id: 'bookings',
      icon: Ticket,
      color: 'text-pink-600 dark:text-pink-400',
      gradient: 'from-pink-500 to-rose-700',
      title: t('docs.bookings.title'),
      subtitle: t('docs.bookings.subtitle'),
      content: [
        { type: 'p', text: t('docs.bookings.p1') },
        { type: 'features', items: [
          { icon: Plane,     label: t('docs.bookings.feat_flights_label'),   desc: t('docs.bookings.feat_flights_desc') },
          { icon: BedDouble, label: t('docs.bookings.feat_hotels_label'),    desc: t('docs.bookings.feat_hotels_desc') },
          { icon: Ticket,    label: t('docs.bookings.feat_bookings_label'),  desc: t('docs.bookings.feat_bookings_desc') },
          { icon: Paperclip, label: t('docs.bookings.feat_docs_label'),      desc: t('docs.bookings.feat_docs_desc') },
        ]},
        { type: 'tip',     text: t('docs.bookings.tip1') },
        { type: 'warning', text: t('docs.bookings.warning1') },
      ],
    },
    {
      id: 'expenses',
      icon: DollarSign,
      color: 'text-green-600 dark:text-green-400',
      gradient: 'from-green-500 to-emerald-700',
      title: t('docs.expenses.title'),
      subtitle: t('docs.expenses.subtitle'),
      content: [
        { type: 'p', text: t('docs.expenses.p1') },
        { type: 'features', items: [
          { icon: Plus,         label: t('docs.expenses.feat_add_label'),    desc: t('docs.expenses.feat_add_desc') },
          { icon: RefreshCw,    label: t('docs.expenses.feat_settle_label'), desc: t('docs.expenses.feat_settle_desc') },
          { icon: CheckCircle2, label: t('docs.expenses.feat_debt_label'),   desc: t('docs.expenses.feat_debt_desc') },
          { icon: Target,       label: t('docs.expenses.feat_budget_label'), desc: t('docs.expenses.feat_budget_desc') },
        ]},
        { type: 'tip', text: t('docs.expenses.tip1') },
        { type: 'steps', items: [
          t('docs.expenses.step1'), t('docs.expenses.step2'), t('docs.expenses.step3'),
          t('docs.expenses.step4'), t('docs.expenses.step5'),
        ]},
      ],
    },
    {
      id: 'info',
      icon: Info,
      color: 'text-amber-600 dark:text-amber-400',
      gradient: 'from-amber-500 to-orange-600',
      title: t('docs.info.title'),
      subtitle: t('docs.info.subtitle'),
      content: [
        { type: 'p', text: t('docs.info.p1') },
        { type: 'features', items: [
          { icon: Info,       label: t('docs.info.feat_tips_label'),       desc: t('docs.info.feat_tips_desc') },
          { icon: ListChecks, label: t('docs.info.feat_checklists_label'), desc: t('docs.info.feat_checklists_desc') },
          { icon: Sparkles,   label: t('docs.info.feat_ai_label'),         desc: t('docs.info.feat_ai_desc') },
        ]},
        { type: 'tip', text: t('docs.info.tip1') },
        { type: 'steps', items: [
          t('docs.info.step1'), t('docs.info.step2'),
          t('docs.info.step3'), t('docs.info.step4'),
        ]},
      ],
    },
    {
      id: 'ai',
      icon: Sparkles,
      color: 'text-rose-600 dark:text-rose-400',
      gradient: 'from-rose-500 to-pink-700',
      title: t('docs.ai.title'),
      subtitle: t('docs.ai.subtitle'),
      content: [
        { type: 'p', text: t('docs.ai.p1') },
        { type: 'features', items: [
          { icon: MapPin,       label: t('docs.ai.feat_places_label'),    desc: t('docs.ai.feat_places_desc') },
          { icon: CalendarDays, label: t('docs.ai.feat_itinerary_label'), desc: t('docs.ai.feat_itinerary_desc') },
          { icon: Route,        label: t('docs.ai.feat_route_label'),     desc: t('docs.ai.feat_route_desc') },
          { icon: ListChecks,   label: t('docs.ai.feat_packing_label'),   desc: t('docs.ai.feat_packing_desc') },
        ]},
        { type: 'tip',     text: t('docs.ai.tip1') },
        { type: 'warning', text: t('docs.ai.warning1') },
      ],
    },
    {
      id: 'sharing',
      icon: Share2,
      color: 'text-cyan-600 dark:text-cyan-400',
      gradient: 'from-cyan-500 to-teal-700',
      title: t('docs.sharing.title'),
      subtitle: t('docs.sharing.subtitle'),
      content: [
        { type: 'p', text: t('docs.sharing.p1') },
        { type: 'features', items: [
          { icon: Globe,  label: t('docs.sharing.feat_enable_label'),  desc: t('docs.sharing.feat_enable_desc') },
          { icon: Share2, label: t('docs.sharing.feat_copy_label'),    desc: t('docs.sharing.feat_copy_desc') },
          { icon: Globe,  label: t('docs.sharing.feat_disable_label'), desc: t('docs.sharing.feat_disable_desc') },
        ]},
        { type: 'tip',     text: t('docs.sharing.tip1') },
        { type: 'warning', text: t('docs.sharing.warning1') },
      ],
    },
    {
      id: 'participants',
      icon: Users,
      color: 'text-orange-600 dark:text-orange-400',
      gradient: 'from-orange-500 to-amber-600',
      title: t('docs.participants.title'),
      subtitle: t('docs.participants.subtitle'),
      content: [
        { type: 'p', text: t('docs.participants.p1') },
        { type: 'features', items: [
          { icon: Users,        label: t('docs.participants.feat_invite_label'), desc: t('docs.participants.feat_invite_desc') },
          { icon: Zap,          label: t('docs.participants.feat_editor_label'), desc: t('docs.participants.feat_editor_desc') },
          { icon: CheckCircle2, label: t('docs.participants.feat_viewer_label'), desc: t('docs.participants.feat_viewer_desc') },
        ]},
        { type: 'tip', text: t('docs.participants.tip1') },
        { type: 'steps', items: [
          t('docs.participants.step1'), t('docs.participants.step2'),
          t('docs.participants.step3'), t('docs.participants.step4'),
        ]},
      ],
    },
    {
      id: 'notifications',
      icon: Bell,
      color: 'text-red-600 dark:text-red-400',
      gradient: 'from-red-500 to-rose-700',
      title: t('docs.notifications.title'),
      subtitle: t('docs.notifications.subtitle'),
      content: [
        { type: 'p', text: t('docs.notifications.p1') },
        { type: 'features', items: [
          { icon: Plane,    label: t('docs.notifications.feat_flights_label'), desc: t('docs.notifications.feat_flights_desc') },
          { icon: BedDouble,label: t('docs.notifications.feat_hotels_label'),  desc: t('docs.notifications.feat_hotels_desc') },
          { icon: Bell,     label: t('docs.notifications.feat_where_label'),   desc: t('docs.notifications.feat_where_desc') },
        ]},
        { type: 'tip', text: t('docs.notifications.tip1') },
      ],
    },
    {
      id: 'pwa',
      icon: Camera,
      color: 'text-indigo-600 dark:text-indigo-400',
      gradient: 'from-indigo-500 to-violet-700',
      title: t('docs.pwa.title'),
      subtitle: t('docs.pwa.subtitle'),
      content: [
        { type: 'p', text: t('docs.pwa.p1') },
        { type: 'features', items: [
          { icon: Plus, label: t('docs.pwa.feat_ios_label'),     desc: t('docs.pwa.feat_ios_desc') },
          { icon: Plus, label: t('docs.pwa.feat_android_label'), desc: t('docs.pwa.feat_android_desc') },
          { icon: Plus, label: t('docs.pwa.feat_desktop_label'), desc: t('docs.pwa.feat_desktop_desc') },
        ]},
        { type: 'tip', text: t('docs.pwa.tip1') },
      ],
    },
  ];
}

// ── Render helpers ─────────────────────────────────────────────────────────────

function BlockContent({ block }: { block: DocBlock }) {
  if (block.type === 'p') {
    return <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{block.text}</p>;
  }

  if (block.type === 'tip') {
    return (
      <div className="flex gap-3 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/50 px-4 py-3">
        <Icon as={Zap} size={16} className="text-teal-500 shrink-0 mt-0.5" />
        <p className="text-sm text-teal-800 dark:text-teal-300 leading-relaxed">{block.text}</p>
      </div>
    );
  }

  if (block.type === 'warning') {
    return (
      <div className="flex gap-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 px-4 py-3">
        <Icon as={Info} size={16} className="text-amber-500 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 dark:text-amber-300 leading-relaxed">{block.text}</p>
      </div>
    );
  }

  if (block.type === 'steps') {
    return (
      <ol className="space-y-2">
        {block.items.map((step, i) => (
          <li key={i} className="flex gap-3 items-start">
            <span className="w-5 h-5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-400 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
              {i + 1}
            </span>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{step}</p>
          </li>
        ))}
      </ol>
    );
  }

  if (block.type === 'features') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {block.items.map(({ icon: FeatureIcon, label, desc }) => (
          <div key={label} className="flex gap-3 rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 px-4 py-3">
            <div className="w-7 h-7 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center shrink-0 shadow-sm">
              <Icon as={FeatureIcon} size={14} className="text-slate-500 dark:text-slate-400" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 mb-0.5">{label}</p>
              <p className="text-xs text-slate-500 dark:text-slate-500 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return null;
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function DocsPage() {
  const { t } = useTranslation();
  const SECTIONS = getSections(t);

  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Update active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter(e => e.isIntersecting);
        if (visible.length > 0) {
          const topmost = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveId(topmost.target.id);
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    );
    Object.values(sectionRefs.current).forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  function scrollTo(id: string) {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
  }

  return (
    <div className="w-full min-h-full bg-slate-50 dark:bg-slate-950">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-14">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors mb-6"
          >
            <Icon as={ArrowLeft} size={14} />
            {t('docs.hero.back')}
          </Link>
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-900/30 shrink-0">
              <Icon as={AlignLeft} size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-2">
                {t('docs.hero.title')}
              </h1>
              <p className="text-slate-400 text-sm md:text-base max-w-xl leading-relaxed">
                {t('docs.hero.subtitle')}
              </p>
            </div>
          </div>

          {/* Quick nav pills */}
          <div className="flex flex-wrap gap-2 mt-8">
            {SECTIONS.map(({ id, icon: SectionIcon, title, color }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                  activeId === id
                    ? 'bg-white/10 border-white/20 text-white'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10 hover:text-slate-200'
                }`}
              >
                <Icon as={SectionIcon} size={12} className={activeId === id ? 'text-white' : color} />
                {title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Body: sidebar + content ──────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 flex gap-8 items-start">

        {/* Left sticky nav (desktop) */}
        <aside className="hidden lg:block w-52 shrink-0 sticky top-6">
          <p className="text-[10px] font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-3 px-3">{t('docs.sidebar.sections')}</p>
          <nav className="space-y-0.5">
            {SECTIONS.map(({ id, icon: SectionIcon, title, color }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all cursor-pointer text-left ${
                  activeId === id
                    ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-medium'
                    : 'text-slate-500 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-300'
                }`}
              >
                <Icon as={SectionIcon} size={14} className={`shrink-0 ${activeId === id ? color : 'text-slate-400 dark:text-slate-600'}`} />
                {title}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0 space-y-6">
          {SECTIONS.map(({ id, icon: SectionIcon, color, gradient, title, subtitle, content }) => (
            <section
              key={id}
              id={id}
              ref={el => { sectionRefs.current[id] = el; }}
              className="card overflow-hidden scroll-mt-6"
            >
              {/* Section header */}
              <div className={`bg-gradient-to-r ${gradient} px-6 py-5 relative overflow-hidden`}>
                <div
                  className="absolute inset-0 opacity-10 pointer-events-none"
                  style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
                />
                <div className="flex items-center gap-3 relative z-10">
                  <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                    <Icon as={SectionIcon} size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-xl text-white leading-tight">{title}</h2>
                    <p className="text-white/70 text-xs mt-0.5">{subtitle}</p>
                  </div>
                </div>
              </div>

              {/* Section body */}
              <div className="px-6 py-6 space-y-5">
                {content.map((block, i) => (
                  <BlockContent key={i} block={block} />
                ))}
              </div>
            </section>
          ))}

          {/* Footer */}
          <div className="card p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shrink-0">
              <Icon as={ChevronRight} size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{t('docs.footer.title')}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t('docs.footer.subtitle')}</p>
            </div>
            <Link to="/dashboard" className="btn-primary shrink-0 gap-1.5">
              {t('docs.footer.cta')}
              <Icon as={ChevronRight} size={16} />
            </Link>
          </div>
        </main>
      </div>
    </div>
  );
}
